import { IconProp } from '@fortawesome/fontawesome-svg-core';
import HlIcon, { HlIconProps } from '@hl-components/Icon/Icon';
import clsx from 'clsx';
import React, { MouseEvent } from 'react';

type HlIconButtonStyle = 'filled' | 'outlined' | 'tertiary';
type HlIconButtonColor =
  | 'primary'
  | 'subtle'
  | 'negative'
  | 'warning'
  | 'info'
  | 'positive'
  | 'contrast';

export type HlIconButtonProps = {
  icon: IconProp;
  size: 'xsmall' | 'small' | 'medium' | 'large';
  styleVariant?: HlIconButtonStyle;
  color?: HlIconButtonColor;
  disabled?: boolean;
  onClick?: (event: MouseEvent) => void;
  'aria-label'?: string;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  tabIndex?: number;
};

const iconSizeMap: Record<HlIconButtonProps['size'], HlIconProps['size']> = {
  xsmall: 'xxsmall',
  small: 'xsmall',
  medium: 'small',
  large: 'medium'
};

export const HlIconButton = ({
  icon,
  size,
  styleVariant = 'filled',
  color = 'primary',
  disabled = false,
  'aria-label': ariaLabel,
  onClick = () => {},
  className,
  type = 'button',
  tabIndex
}: HlIconButtonProps) => {
  return (
    <button
      className={clsx('hl-icon-button', className, {
        'hl-filled': styleVariant === 'filled',
        'hl-outlined': styleVariant === 'outlined',
        'hl-tertiary': styleVariant === 'tertiary',

        'hl-primary': color === 'primary',
        'hl-subtle': color === 'subtle',
        'hl-negative': color === 'negative',
        'hl-warning': color === 'warning',
        'hl-info': color === 'info',
        'hl-positive': color === 'positive',
        'hl-contrast': color === 'contrast',

        'is-xsmall-sized hl-corners-micro': size === 'xsmall',
        'is-small-sized hl-corners-tiny': size === 'small',
        'is-medium-sized hl-corners-tiny': size === 'medium',
        'is-large-sized hl-corners-xxsmall': size === 'large'
      })}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}
      type={type}
      tabIndex={tabIndex}
    >
      <HlIcon icon={icon} size={iconSizeMap[size]} />
    </button>
  );
};

export default HlIconButton;
